import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Stack,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr } from 'date-fns/locale';
import { Save, Cancel } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import MyModal from '../MyModal/MyModal';
import { jobStatusUpdateModalStyles } from './JobStatusUpdateModal.styles';
import ProjectDatesSection from './ProjectDatesSection';
import ProjectInfoEditSection from './ProjectInfoEditSection';
import JobHistorySection from './JobHistorySection';
import {
  UPDATE_JOB_STATUS,
  UPDATE_JOB_TEKNOPARK_UNIT,
  CREATE_JOB_DETAIL,
} from '../../graphql/mutations/jobs';
import graphqlClient from '../../utils/graphqlClient';
import { formatDateForHasura } from '../../utils/dateUtils';
import { usePartners } from '../../hooks/usePartners';
import { useAuth } from '../../hooks';
import TextInputField from '../FormField/TextInputField';
import FileUploadField from '../FormField/FileUploadField';
const JobStatusUpdateModal = ({ open, onClose, job, onSuccess, onError, onRefresh }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { partners } = usePartners();

  // Yeni iliski job.job_type relationship objesi ile bilgi tasiyor.
  // is_project_based ve status_group oncelikle yeni alandan okunur;
  // eski isler icin contract.contract_subject.is_project_based fallback'i
  // korunuyor ki transition sirasinda davranis bozulmasin.
  const isProjectBased =
    job?.job_type?.is_project_based ?? job?.contract?.contract_subject?.is_project_based;

  // Legacy enum metni — job_type relationship objesi uzerinde tutulan
  // name FK yokken transformData sayesinde enum'a fallback yapar.
  const jobTypeEnum = job?.job_type_enum || job?.job_type?.name || null;

  const getStatusesByGroup = (statusGroup, jobTypeName) => {
    // Data-driven secim: yeni iliskide status_group varsa onu kullaniyoruz.
    if (statusGroup === 'phased') {
      return [
        'ORTAK_BULUNDU',
        'ASAMA_BIR_YAZIMDA',
        'ASAMA_BIR_DEGERLENDIRMEDE',
        'ASAMA_BIR_ONAYLANDI',
        'ASAMA_IKI_YAZIMDA',
        'ASAMA_IKI_DEGERLENDIRMEDE',
        'ASAMA_IKI_ONAYLANDI',
        'DEGERLENDIRMEDE',
        'ONAYLANDI',
        'REDDEDILDI',
        'KAPATILDI',
        'DANISMANLIK_SONLANDIRILDI',
      ];
    }
    if (statusGroup === 'standard') {
      return [
        'YAZIMDA',
        'DEGERLENDIRMEDE',
        'ONAYLANDI',
        'REDDEDILDI',
        'KAPATILDI',
        'DANISMANLIK_SONLANDIRILDI',
        'TAMAMLANDI',
      ];
    }
    // statusGroup yoksa (eski isler), enum adina gore legacy switch'e fallback.
    switch (jobTypeName) {
      case 'TEKNOPARK':
      case 'AR_GE':
      case 'TASARIM_MERKEZI':
      case 'TUBITAK':
        return [
          'YAZIMDA',
          'DEGERLENDIRMEDE',
          'ONAYLANDI',
          'REDDEDILDI',
          'KAPATILDI',
          'DANISMANLIK_SONLANDIRILDI',
          'TAMAMLANDI',
        ];
      case 'ULUSLARARASI':
        return [
          'ORTAK_BULUNDU',
          'ASAMA_BIR_YAZIMDA',
          'ASAMA_BIR_DEGERLENDIRMEDE',
          'ASAMA_BIR_ONAYLANDI',
          'ASAMA_IKI_YAZIMDA',
          'ASAMA_IKI_DEGERLENDIRMEDE',
          'ASAMA_IKI_ONAYLANDI',
          'DEGERLENDIRMEDE',
          'ONAYLANDI',
          'REDDEDILDI',
          'KAPATILDI',
          'DANISMANLIK_SONLANDIRILDI',
        ];
      default:
        return [
          'YAZIMDA',
          'DEGERLENDIRMEDE',
          'ONAYLANDI',
          'DEVRALINDI',
          'REDDEDILDI',
          'KAPATILDI',
          'DANISMANLIK_SONLANDIRILDI',
        ];
    }
  };

  const getAvailableStatuses = () => {
    return getStatusesByGroup(job?.job_type?.status_group, jobTypeEnum);
  };

  const getRequiredFields = (jobTypeName, newStatus) => {
    const fields = {};

    switch (jobTypeName) {
      case 'TEKNOPARK':
      case 'AR_GE':
      case 'TASARIM_MERKEZI':
        if (newStatus === 'DEGERLENDIRMEDE') {
          fields.submissionDate = true;
        } else if (newStatus === 'ONAYLANDI') {
          fields.approvalDate = true;
          // Yeni is_project_based bayragi job_type iliskisinden okunur;
          // eski isler icin contract_subject fallback'i korunuyor.
          if (job?.job_type?.is_project_based ?? job?.contract?.contract_subject?.is_project_based) {
            fields.projectStartDate = true;
            fields.projectEndDate = true;
          }
          fields.commissionDocument = true;

        } else if (newStatus === 'REDDEDILDI') {
          fields.rejectionDate = true;
          fields.rejectionReason = true;
          fields.commissionDocument = true;
        } else if (
          newStatus === 'KAPATILDI' ||
          newStatus === 'DANISMANLIK_SONLANDIRILDI'
        ) {
          fields.officialClosingDate = true;
          fields.lastWorkDate = true;
        }
        break;

      case 'TUBITAK':
        if (newStatus === 'DEGERLENDIRMEDE') {
          fields.submissionDate = true;
        } else if (newStatus === 'ONAYLANDI') {
          fields.approvalDate = true;
          // Yeni is_project_based bayragi job_type iliskisinden okunur;
          // eski isler icin contract_subject fallback'i korunuyor.
          if (job?.job_type?.is_project_based ?? job?.contract?.contract_subject?.is_project_based) {
            fields.projectStartDate = true;
            fields.projectEndDate = true;
          }
          fields.commissionDocument = true;
        } else if (newStatus === 'REDDEDILDI') {
          fields.rejectionDate = true;
          fields.rejectionReason = true;
          fields.commissionDocument = true;
        } else if (
          newStatus === 'KAPATILDI' ||
          newStatus === 'DANISMANLIK_SONLANDIRILDI'
        ) {
          fields.projectEndDate = true;
        }
        break;

      case 'ULUSLARARASI':
        if (newStatus === 'ORTAK_BULUNDU') {
          fields.noPartnerNeeded = true;
          fields.partnerFirmId = true;
        } else if (newStatus === 'ASAMA_BIR_DEGERLENDIRMEDE') {
          fields.submissionDate = true;
        } else if (newStatus === 'ASAMA_IKI_YAZIMDA') {
          fields.firstPhaseDocument = true;
        } else if (newStatus === 'ASAMA_IKI_ONAYLANDI') {
          fields.approvalDate = true;
          // Yeni is_project_based bayragi job_type iliskisinden okunur;
          // eski isler icin contract_subject fallback'i korunuyor.
          if (job?.job_type?.is_project_based ?? job?.contract?.contract_subject?.is_project_based) {
            fields.projectStartDate = true;
            fields.projectEndDate = true;
          }
          fields.commissionDocument = true;

        } else if (newStatus === 'DEGERLENDIRMEDE') {
          fields.submissionDate = true;
        } else if (newStatus === 'ONAYLANDI') {
          fields.approvalDate = true;
          // Yeni is_project_based bayragi job_type iliskisinden okunur;
          // eski isler icin contract_subject fallback'i korunuyor.
          if (job?.job_type?.is_project_based ?? job?.contract?.contract_subject?.is_project_based) {
            fields.projectStartDate = true;
            fields.projectEndDate = true;
          }
          fields.commissionDocument = true;

        } else if (newStatus === 'REDDEDILDI') {
          fields.rejectionDate = true;
          fields.rejectionReason = true;
          fields.commissionDocument = true;
        } else if (
          newStatus === 'KAPATILDI' ||
          newStatus === 'DANISMANLIK_SONLANDIRILDI'
        ) {
          fields.projectEndDate = true;
        }
        break;

      default:
        if (newStatus === 'DEGERLENDIRMEDE') {
          fields.submissionDate = true;
        } else if (newStatus === 'ONAYLANDI') {
          fields.approvalDate = true;
          // Yeni is_project_based bayragi job_type iliskisinden okunur;
          // eski isler icin contract_subject fallback'i korunuyor.
          if (job?.job_type?.is_project_based ?? job?.contract?.contract_subject?.is_project_based) {
            fields.projectStartDate = true;
            fields.projectEndDate = true;
          }
          fields.commissionDocument = true;

        } else if (newStatus === 'REDDEDILDI') {
          fields.rejectionDate = true;
          fields.rejectionReason = true;
          fields.commissionDocument = true;
        } else if (
          newStatus === 'KAPATILDI' ||
          newStatus === 'DANISMANLIK_SONLANDIRILDI'
        ) {
          fields.projectEndDate = true;
        }
        break;
    }

    return fields;
  };

  const availableStatuses = useMemo(
    () => getAvailableStatuses(),
    // status_group ve enum adi degisirse statu listesi de yeniden hesaplansin.
    [job?.job_type?.status_group, jobTypeEnum]
  );

  const requiredFields = useMemo(
    () => getRequiredFields(jobTypeEnum, formData.newStatus),
    [jobTypeEnum, formData.newStatus]
  );

  useEffect(() => {
    if (open && job) {
      setFormData({
        newStatus: '',
        notes: '',
        submissionDate: '',
        approvalDate: '',
        rejectionDate: '',
        rejectionReason: '',
        projectNumber:
          job.project_number !== null && job.project_number !== undefined
            ? job.project_number.toString()
            : '',
        projectStartDate: '',
        projectEndDate: '',
        teknoparkUnit: job.teknopark_unit || '',
        commissionDocument: null,
        firstPhaseDocument: null,
        officialClosingDate: '',
        lastWorkDate: '',
        noPartnerNeeded: null,
        partnerFirmId: '',
      });
      setError(null);
    }
  }, [open, job]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = () => {
    const hasExistingProjectNumber =
      job?.project_number !== null && job?.project_number !== undefined && job?.project_number !== '';

    if (
      jobTypeEnum === 'TUBITAK' &&
      formData.newStatus &&
      formData.newStatus !== 'YAZIMDA' &&
      !hasExistingProjectNumber &&
      !formData.projectNumber
    ) {
      return `${t('jobStatusUpdate.fields.projectNumber')} ${t('common.required')}`;
    }

    for (const [field, required] of Object.entries(requiredFields)) {
      if (required) {
        if (field === 'noPartnerNeeded') {
          if (formData[field] === undefined || formData[field] === null || formData[field] === '') {
            return `${t(`jobStatusUpdate.fields.${field}`)} ${t('common.required')}`;
          }
        }
        else if (!formData[field]) {
          if (field === 'partnerFirmId' && formData.noPartnerNeeded === true) {
            continue;
          }

          if (
            field === 'commissionDocument' &&
            // Yeni iliskiden is_project_based oncelikli; eski isler icin
            // contract_subject fallback'i devrede kalir.
            !(job?.job_type?.is_project_based ?? job?.contract?.contract_subject?.is_project_based)
          ) {
            continue;
          }
          return `${t(`jobStatusUpdate.fields.${field}`)} ${t('common.required')}`;
        }
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    const teknoparkUnitValue = jobTypeEnum === 'TEKNOPARK'
      ? (formData.teknoparkUnit || '').trim().slice(0, 15) || null
      : (job?.teknopark_unit ?? null);
    const teknoparkChanged = teknoparkUnitValue !== (job?.teknopark_unit ?? null);

    // Statu secilmemisse: teknopark birimi degistiyse yalnizca onu kaydet
    // (statu secme zorunlulugu yok), degismediyse kullanicidan statu iste.
    if (!formData.newStatus) {
      if (teknoparkChanged) {
        setLoading(true);
        setError(null);
        try {
          await graphqlClient.mutate(UPDATE_JOB_TEKNOPARK_UNIT, {
            id: job.id,
            teknopark_unit: teknoparkUnitValue,
          });
          onSuccess?.(t('jobStatusUpdate.teknoparkUnitUpdateSuccess', 'Teknopark birimi güncellendi'));
          onRefresh?.();
          onClose();
        } catch (err) {
          const errorMessage = err.message || t('jobStatusUpdate.errorMessage');
          setError(errorMessage);
          onError?.(errorMessage);
        } finally {
          setLoading(false);
        }
        return;
      }
      setError(t('jobStatusUpdate.statusRequired', 'Lütfen bir durum seçin'));
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const projectNumberValue =
        formData.projectNumber && formData.projectNumber !== ''
          ? parseInt(formData.projectNumber, 10)
          : null;

      await graphqlClient.mutate(UPDATE_JOB_STATUS, {
        id: job.id,
        job_status: formData.newStatus,
        project_number: Number.isNaN(projectNumberValue)
          ? null
          : projectNumberValue,
        teknopark_unit: teknoparkUnitValue,
      });

      const detailData = {
        notes: formData.notes,
        oldStatus: job.job_status,
        newStatus: formData.newStatus,
        statusChange: `${job.job_status} → ${formData.newStatus}`,
        submissionDate: formData.submissionDate || null,
        approvalDate: formData.approvalDate || null,
        rejectionDate: formData.rejectionDate || null,
        rejectionReason: formData.rejectionReason || null,
        projectNumber: formData.projectNumber || null,
        projectStartDate: formData.projectStartDate || null,
        projectEndDate: formData.projectEndDate || null,
        teknoparkUnit: jobTypeEnum === 'TEKNOPARK' ? teknoparkUnitValue : null,
        commissionDocument: formData.commissionDocument?.key || null,
        firstPhaseDocument: formData.firstPhaseDocument?.key || null,
        officialClosingDate: formData.officialClosingDate || null,
        lastWorkDate: formData.lastWorkDate || null,
      };

      if (formData.newStatus === 'ORTAK_BULUNDU') {
        detailData.noPartnerNeeded = formData.noPartnerNeeded !== null && formData.noPartnerNeeded !== undefined ? formData.noPartnerNeeded : null;
        detailData.partnerFirmId = formData.noPartnerNeeded === false ? formData.partnerFirmId || null : null;
      }

      await graphqlClient.mutate(CREATE_JOB_DETAIL, {
        job_id: job.id,
        job_detail_type: jobTypeEnum,
        detail_data: detailData,
        created_by: user?.id,
      });

      const successMessage = t('jobStatusUpdate.successMessage', {
        jobTitle: job.job_title,
        newStatus: t(`jobs.jobStatuses.${formData.newStatus}`),
      });

      onSuccess?.(successMessage);
      onRefresh?.();
      onClose();
    } catch (err) {
      const errorMessage = err.message || t('jobStatusUpdate.errorMessage');
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderField = field => {
    if (!requiredFields[field]) return null;

    // Yeni iliskiden oncelikli oku; eski isler icin contract_subject fallback'i.
    const fieldIsProjectBased =
      job?.job_type?.is_project_based ?? job?.contract?.contract_subject?.is_project_based;
    const isOptionalField = field === 'commissionDocument' && !fieldIsProjectBased;

    const fieldConfig = {
      name: field,
      label: `jobStatusUpdate.fields.${field}`,
      required: !isOptionalField,
    };

    switch (field) {
      case 'submissionDate':
      case 'approvalDate':
      case 'rejectionDate':
      case 'projectStartDate':
      case 'projectEndDate':
      case 'officialClosingDate':
      case 'lastWorkDate':
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
            <DatePicker
              label={t(`jobStatusUpdate.fields.${field}`)}
              value={formData[field] ? new Date(formData[field]) : null}
              onChange={(newValue) => {
                const dateString = formatDateForHasura(newValue) || '';
                handleInputChange(field, dateString);
              }}
              renderInput={(params) => <TextField {...params} fullWidth required={!isOptionalField} />}
              format="dd.MM.yyyy"
              views={['year', 'month', 'day']}
            />
          </LocalizationProvider>
        );

      case 'commissionDocument':
      case 'firstPhaseDocument':
        return (
          <Box sx={{ 
            width: '100%',
            '& .MuiBox-root': {
              width: '100%'
            }
          }}>
            <FileUploadField
              field={{ ...fieldConfig, name: field }}
              value={formData[field] || null}
              onChange={(fieldName, value) => handleFileChange(fieldName, value)}
              entityId={job?.id}
              error={null}
            />
          </Box>
        );

      case 'noPartnerNeeded':
          return (
            <FormControl sx={{ minWidth: '250px' }} fullWidth required={!isOptionalField} style={{marginBottom: '10px'}}>
              <InputLabel>{t(`jobStatusUpdate.fields.${field}`)}</InputLabel>
              <Select
                value={formData[field] ? 'true' : 'false'}
                onChange={e =>
                  handleInputChange(field, e.target.value === 'true')
                }
                label={t(`jobStatusUpdate.fields.${field}`)}
              >
                <MenuItem value="true">{t('common.yes')}</MenuItem>
                <MenuItem value="false">{t('common.no')}</MenuItem>
              </Select>
            </FormControl>
          );

      case 'partnerFirmId':
        if (formData.noPartnerNeeded === false) {
          return (
            <FormControl fullWidth required={!isOptionalField}>
              <InputLabel>{t(`jobStatusUpdate.fields.${field}`)}</InputLabel>
              <Select
                value={formData[field] || ''}
                onChange={e => handleInputChange(field, e.target.value)}
                label={t(`jobStatusUpdate.fields.${field}`)}
              >
                <MenuItem value="">
                  <em>{t('common.select')}</em>
                </MenuItem>
                {partners?.map(partner => (
                  <MenuItem key={partner.id} value={partner.id}>
                    {partner.partner_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }
        return null;

      default:
        return (
          <TextInputField
            field={fieldConfig}
            value={formData[field] || ''}
            onChange={(fieldName, value) => handleInputChange(fieldName, value)}
            error={null}
          />
        );
    }
  };

  if (!job) return null;

  return (
    <MyModal
      open={open}
      onClose={onClose}
      title={t('jobStatusUpdate.title')}
      maxWidth="md"
      fullWidth
      disableBackdropClose
      disableEscapeKeyDown
    >
      <Box sx={jobStatusUpdateModalStyles.container}>
        <Paper elevation={2} sx={jobStatusUpdateModalStyles.statusSection}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" color="text.primary">
              {t('jobStatusUpdate.currentStatus')}
            </Typography>
            <Chip
              onClick={() => {}}
              label={job?.job_status ? `${job.job_status}` : '-'}
              color="primary"
              variant="filled"
            />
          </Stack>
          {(() => {
            let approvalDate = null;
            let projectStartDate = null;
            let projectEndDate = null;
            if (job?.job_details) {
              for (const detail of job.job_details) {
                const d = detail.detail_data;
                if (d?.approvalDate && !approvalDate) approvalDate = d.approvalDate;
                if (d?.projectStartDate && !projectStartDate) projectStartDate = d.projectStartDate;
                if (d?.projectEndDate && !projectEndDate) projectEndDate = d.projectEndDate;
              }
            }
            const extensions = Array.isArray(job?.project_extensions) ? job.project_extensions : [];
            const otelme = extensions.find(e => e.type === 'OTELME');
            const uzatmalar = extensions.filter(e => e.type === 'UZATMA');
            if (otelme) {
              if (otelme.startDate) projectStartDate = otelme.startDate;
              if (otelme.endDate) projectEndDate = otelme.endDate;
            }
            if (uzatmalar.length > 0) {
              const lastUzatma = uzatmalar[uzatmalar.length - 1];
              if (lastUzatma.endDate) projectEndDate = lastUzatma.endDate;
            }
            const fmt = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : null;
            const items = [
              job?.project_number != null && { label: t('jobStatusUpdate.fields.projectNumber'), value: job.project_number },
              approvalDate && { label: t('jobStatusUpdate.fields.approvalDate'), value: fmt(approvalDate) },
              projectStartDate && { label: t('jobStatusUpdate.fields.projectStartDate'), value: fmt(projectStartDate) },
              projectEndDate && { label: t('jobStatusUpdate.fields.projectEndDate'), value: fmt(projectEndDate) },
            ].filter(Boolean);
            if (!items.length) return null;
            return (
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                <Stack direction="row" flexWrap="wrap" gap={2.5}>
                  {items.map(item => (
                    <Box key={item.label}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.label}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            );
          })()}
        </Paper>

        {isProjectBased && (
          <ProjectDatesSection job={job} onSuccess={onSuccess} onError={onError} onRefresh={onRefresh} styles={jobStatusUpdateModalStyles} />
        )}

        <ProjectInfoEditSection
          job={job}
          isProjectBased={Boolean(isProjectBased)}
          jobTypeEnum={jobTypeEnum}
          onSuccess={onSuccess}
          onError={onError}
          onRefresh={onRefresh}
          styles={jobStatusUpdateModalStyles}
        />

        <JobHistorySection jobDetails={job?.job_details} projectExtensions={job?.project_extensions} styles={jobStatusUpdateModalStyles} />

        <Divider sx={jobStatusUpdateModalStyles.divider} />

        <Box sx={jobStatusUpdateModalStyles.section}>
          <Typography
            variant="h6"
            color="text.primary"
            sx={jobStatusUpdateModalStyles.sectionTitle}
          >
            {t('jobStatusUpdate.newStatus')}
          </Typography>

          {availableStatuses.length > 0 ? (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t('jobStatusUpdate.selectStatus')}</InputLabel>
                <Select
                  value={formData.newStatus || ''}
                  onChange={e => handleInputChange('newStatus', e.target.value)}
                  label={t('jobStatusUpdate.selectStatus')}
                >
                  {availableStatuses.map(status => (
                    <MenuItem key={status} value={status}>
                      {t(`jobs.jobStatuses.${status}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {jobTypeEnum === 'TUBITAK' &&
                !(job.project_number !== null && job.project_number !== undefined && job.project_number !== '') && (
                  <TextField
                    fullWidth
                    label={t('jobStatusUpdate.fields.projectNumber')}
                    value={formData.projectNumber || ''}
                    onChange={e => handleInputChange('projectNumber', e.target.value)}
                    required={
                      formData.newStatus && formData.newStatus !== 'YAZIMDA'
                    }
                    sx={{ mt: 1 }}
                  />
                )}
            </>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('jobStatusUpdate.noAvailableStatus')}
            </Alert>
          )}
        </Box>

        {formData.newStatus && Object.keys(requiredFields).length > 0 && (
          <>
            <Divider sx={jobStatusUpdateModalStyles.divider} />
            <Box sx={jobStatusUpdateModalStyles.section}>
              <Typography
                variant="h6"
                color="text.primary"
                sx={jobStatusUpdateModalStyles.sectionTitle}
              >
                {t('jobStatusUpdate.requiredFields')}
              </Typography>
              <Grid container spacing={3}>
                {Object.keys(requiredFields).map(field => {
                  if (field === 'noPartnerNeeded') {
                    return (
                      <Grid item xs={12} sm={6} key={field}>
                        {renderField(field)}
                        {!formData.noPartnerNeeded &&
                          requiredFields.partnerFirmId &&
                          renderField('partnerFirmId')}
                      </Grid>
                    );
                  }
                  if (field === 'partnerFirmId') {
                    return null;
                  }
                  // File upload alanları için tam genişlik
                  if (field === 'commissionDocument' || field === 'firstPhaseDocument') {
                    return (
                      <Grid item xs={12} key={field}>
                        {renderField(field)}
                      </Grid>
                    );
                  }
                  return (
                    <Grid item xs={12} sm={6} key={field}>
                      {renderField(field)}
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </>
        )}

        {/* Teknopark Birimi — statu secme sarti yok, her zaman gorunur.
             Ana Guncelle butonuyla kaydedilir; statu secilmemisse yalnizca
             bu alan guncellenir. */}
        {jobTypeEnum === 'TEKNOPARK' && (
          <>
            <Divider sx={jobStatusUpdateModalStyles.divider} />
            <Box sx={jobStatusUpdateModalStyles.section}>
              <Typography
                variant="h6"
                color="text.primary"
                sx={jobStatusUpdateModalStyles.sectionTitle}
              >
                {t('jobStatusUpdate.fields.teknoparkUnit')}
              </Typography>
              <TextField
                fullWidth
                label={t('jobStatusUpdate.fields.teknoparkUnit')}
                value={formData.teknoparkUnit || ''}
                onChange={e => handleInputChange('teknoparkUnit', e.target.value.slice(0, 15))}
                slotProps={{ htmlInput: { maxLength: 15 } }}
                helperText={`${(formData.teknoparkUnit || '').length}/15`}
                sx={{ mt: 1 }}
              />
            </Box>
          </>
        )}

        <Divider sx={jobStatusUpdateModalStyles.divider} />
        <Box sx={jobStatusUpdateModalStyles.section}>
          <Typography
            variant="h6"
            color="text.primary"
            sx={jobStatusUpdateModalStyles.sectionTitle}
          >
            {t('jobStatusUpdate.notes')}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t('jobStatusUpdate.notesPlaceholder')}
            value={formData.notes || ''}
            onChange={e => handleInputChange('notes', e.target.value)}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={jobStatusUpdateModalStyles.actions}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={loading}
            startIcon={<Cancel />}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || availableStatuses.length === 0}
            startIcon={loading ? <CircularProgress size={20} /> : <Save />}
          >
            {loading ? t('common.loading') : t('jobStatusUpdate.update')}
          </Button>
        </Box>
      </Box>
    </MyModal>
  );
};

export default JobStatusUpdateModal;
